import { IonSpinner } from '@ionic/react'
import { useQueryClient } from '@tanstack/react-query'
import { $Enums, NewContract } from '@vae/server/dist/client'
import { DHCustomerData } from '@vae/server/src/modules/ExternalAPIs/DataHub/types/datahub.interface'
import { FixedProductDetails } from '@vae/server/src/types/synerallApiTypes'
import dayjs from 'dayjs'
import { default as React, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../analytics/utils/firebaseUtils'
import { decodeBase64 } from '../../components/mainTabs/WebAppLayout'
import Stepper, { StepConfig } from '../../components/stepper/Stepper'
import useUser from '../../contexts/hooks/useUser'
import { ModalContext } from '../../contexts/ModalContext'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../contexts/UserContext'
import i18n from '../../i18n'
import { backendCall } from '../../modules/backendApi'
import { trpc } from '../../modules/trpc'
import MeteringPointInformationForm from '../../pages/contact/moveOutWizard/MeteringPointInformationForm'
import {
	AuthenticateStep,
	MoveOutFormFields,
} from '../../pages/contact/moveOutWizard/MoveOutWizard'
import ModalWrapper from '../../wrappers/modalWrapper/ModalWrapper'
import BillingAddressDetailsStep from './steps/BillingAddressDetailsStep'
import BillingMethodStep from './steps/BillingMethodStep'
import ConsumptionPointDetailsStep from './steps/ConsumptionPointDetailsStep'
import CustomerDetailsStep from './steps/CustomerDetailsStep'
import LockInDetailsStep from './steps/lockInDetailsStep/LockInDetailsStep'
import MoveOrChangeStep from './steps/MoveOrChangeStep'
import NewContractTypeStep from './steps/NewContractTypeStep'
import ProductDetailsStep from './steps/ProductDetailsStep'
import TermsAndConditionsStep from './steps/TermsAndConditionsStep'

const dateFormat = 'YYYY-MM-DD'

const CreateContractModal: React.FC = () => {
	// Contexts
	const { streetAddress, city, zipCode, houseNumber, gsrn } = useContext(ObjectContext)
	const { isAnonymousUser, strongAuthenticationDetails, postAuthProductId, authExpire } =
		useContext(UserContext)

	const { activeOffer, setActiveOffer } = useContext(ModalContext)
	const { id } = useContext(ObjectContext)

	const { data: products } = trpc.synerall.productOffers.useQuery({
		objectId: id,
	})

	useEffect(() => {
		if (!activeOffer && postAuthProductId && postAuthProductId !== 'null') {
			// Find product from data
			const offer = products?.offers?.find(offer => offer.product?.ID === Number(postAuthProductId))
			if (offer) {
				setActiveOffer(offer)
			}
		}
	}, [postAuthProductId, products, setActiveOffer])
	// States
	const [contract, setContract] = useState<Partial<NewContract>>()
	const form = useForm<MoveOutFormFields>({ mode: 'onBlur' })
	const [dhUser, setDhUser] = useState<DHCustomerData>()
	const [seperateBillingAddress, setSeperateBillingAddress] = useState<boolean>(false)
	const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
	const [activeStep, setActiveStep] = useState(0)

	// Hooks
	const { t } = useTranslation('modals_createContract')
	const { t: tcm } = useTranslation('modals_moveOutContact')

	const { user } = useUser()

	const queryClient = useQueryClient()

	const removeDoubleWhitespaces = (input: string): string => {
		return input.replace(/\s{2,}/g, ' ')
	}

	// Variables
	const product = activeOffer?.product
	const meteringPoints = dhUser?.Agreements?.map(x => x.MeteringPoint)
	const meteringPoint = meteringPoints?.find(x => {
		const address =
			`${x.MeteringPointAddress.StreetName ?? ''} ${x.MeteringPointAddress.BuildingNumber ?? ''} ${x.MeteringPointAddress.StairwellIdentification ?? ''} ${x.MeteringPointAddress.Apartment ?? ''}`.trim()
		return removeDoubleWhitespaces(address) === streetAddress
	})

	// Queries
	const { data: priceLockContracts } = trpc.synerall.priceLock.useQuery({
		productId: product?.ID ?? 0,
	})

	// Functions
	const handleNext = async () => {
		logEvent(`click-create-offer-next-step-${activeStep + 1}`)
		setActiveStep(prevActiveStep => prevActiveStep + 1)
	}

	const handlePrev = () => {
		logEvent(`click-create-offer-previous-step-${activeStep - 1}`)
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}

	const onContractChange = (key: keyof NewContract, value: string | number | boolean) => {
		setContract(prev => ({ ...prev, [key]: value }))
	}

	const getDatahubUser = async () => {
		if (isAnonymousUser) return

		const dhUser = await queryClient.fetchQuery(
			['dhCustomer', user?.hashedPid ?? strongAuthenticationDetails?.personalId ?? ''],
			() => backendCall.users.getDatahubCustomer(user?.hashedPid ?? '').catch(() => null),
			{ staleTime: 1000 }
		)

		if (dhUser) setDhUser(dhUser)
	}

	// Effects
	useEffect(() => {
		if (dhUser && product) window.scrollTo(0, -100)
	}, [product, dhUser])

	useEffect(() => {
		if (priceLockContracts?.length) {
			steps.find(x => x.key === 'lockInDetails')!.disabled = true
		}
	}, [priceLockContracts])

	useEffect(() => {
		if (!dhUser) {
			getDatahubUser()
		}

		return () => {
			setActiveOffer(undefined)
			setDhUser(undefined)
		}
	}, [])

	const setLockIn = (contract: FixedProductDetails | null) => {
		const texts = user?.language === $Enums.Language.en ? contract?.texts.en : contract?.texts.fi
		const prices = user?.language === $Enums.Language.en ? contract?.prices.en : contract?.prices.fi

		if (contract) {
			setContract(prev => ({
				...prev,
				isLockIn: true,
				lockInName: texts?.title,
				lockInPeriod: texts?.subtitle,
				lockInProductId: contract.productId,
				lockInHourlyPrice: prices?.hourlyPrice,
				lockInMonthlyPrice: prices?.monthlyPrice,
				lockInValidFrom: contract.validFrom,
				lockInValidTo: contract.validTo,
				lockInRatio: prices?.fixedRatio,
			}))
		} else {
			setContract(prev => ({
				...prev,
				isLockIn: false,
				lockInName: null,
				lockInPeriod: null,
				lockInProductId: null,
				lockInHourlyPrice: null,
				lockInMonthlyPrice: null,
				lockInValidFrom: null,
				lockInValidTo: null,
				lockInRatio: null,
			}))
		}
	}

	useEffect(() => {
		// Init newContract
		if ((strongAuthenticationDetails || (user && dhUser)) && activeOffer?.product) {
			const a = meteringPoint?.MeteringPointAddress
			const product = activeOffer.product
			// Same or seperate billing address init.
			setSeperateBillingAddress(false)

			setContract({
				email: user?.email ?? '',
				contractStartDate: dayjs().add(15, 'day').format(dateFormat),
				firstName: dhUser?.GivenName ?? strongAuthenticationDetails?.firstNames ?? '',
				lastName: dhUser?.FamilyName ?? strongAuthenticationDetails?.surName ?? '',
				ssn: dhUser?.CustomerIdentification ?? strongAuthenticationDetails?.personalId ?? '',
				phone: dhUser?.TelephoneNumber ?? '',
				invoiceType: 'email',
				newContractType: $Enums.NewContractType.moveIn,
				noAds: true,
				streetAddress: a?.StreetName ?? streetAddress ?? '',
				houseNumber: a?.BuildingNumber ?? houseNumber ?? '',
				stairwellIdentification: a?.StairwellIdentification ?? '',
				apartment: a?.Apartment ?? '',
				zipCode: a?.PostalCode ?? zipCode ?? '',
				city: a?.PostOffice ?? city ?? '',
				gsrn: meteringPoint?.MeteringPointEAN ?? gsrn ?? '',
				invoicestreetAddress: '',
				invoicehouseNumber: '',
				invoicestairwellIdentification: '',
				invoiceapartment: '',
				invoicezipCode: '',
				invoicecity: '',
				recommenderEmail: '',
				productId: product.ID,
				productName: product.Titles.find(x => x.Lang.toLowerCase() === i18n.language)?.Value ?? '',
				creationDate: dayjs().toISOString(),
				language: i18n.language,
				isLockIn: false,
				lockInName: null,
				lockInPeriod: null,
				lockInProductId: null,
				lockInHourlyPrice: null,
				lockInMonthlyPrice: null,
				lockInValidFrom: null,
				lockInValidTo: null,
				lockInRatio: null,
				isContractUpdate: false,
			} satisfies Partial<NewContract>)
		}
	}, [dhUser, strongAuthenticationDetails, activeOffer])

	const isAuthDisabled = useMemo(() => {
		if (!authExpire) return false

		// If you have bank authed, we created a user for you, only thing we check is if you are in a bank authed session still
		try {
			const expireDate = dayjs(decodeBase64(authExpire))

			// If current time is not before expire date, disable auth step
			return expireDate.isAfter(dayjs())
		} catch (e) {
			console.error('Failed to decode authExpire, disabling authentication step', e)
			return false
		}
	}, [authExpire])

	const nameNeeded = !dhUser?.GivenName || !strongAuthenticationDetails?.firstNames
	const ssnNeeded = !dhUser?.CustomerIdentification || !strongAuthenticationDetails?.personalId

	const steps: StepConfig<MoveOutFormFields>[] = [
		{
			label: tcm('authenticateStep.authenticate'),
			key: 'AuthenticateStep',
			element: <AuthenticateStep redirectUri='/contact/create-contract' />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: isAuthDisabled,
			disableNext: () => true,
			hideNext: true,
		},
		{
			label: t('productDetailsLabel'),
			key: 'productDetails',
			element: <ProductDetailsStep product={product} />,
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
		},
		{
			label: t('customerDetailsLabel'),
			key: 'customerDetails',
			element: (
				<CustomerDetailsStep
					contract={{ ...contract }}
					setContract={onContractChange}
					nameNeeded={nameNeeded}
					ssnNeeded={ssnNeeded}
				/>
			),
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disableNext: () => (!contract?.email && !contract?.phone) || !contract?.ssn,
		},
		{
			label: t('consumptionPointDetailsLabel'),
			key: 'consumptionPointDetails',
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			element: isAnonymousUser ? (
				<MeteringPointInformationForm formState={form} nextSlide={handleNext} />
			) : (
				<ConsumptionPointDetailsStep
					streetAddress={streetAddress ?? ''}
					city={city ?? ''}
					zipCode={zipCode ?? ''}
					meteringPoint={meteringPoint}
				/>
			),
		},
		{
			label: t('billingAdressDetailsLabel'),
			key: 'billingAdressDetails',
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			element: (
				<BillingAddressDetailsStep
					seperateBillingAddress={seperateBillingAddress}
					setSeperateBillingAddress={setSeperateBillingAddress}
					contract={contract ?? {}}
					setContract={value => setContract(value as Partial<NewContract>)}
				/>
			),
		},
		{
			label: t('newContractTypeLabel'),
			key: 'newContractType',
			ref: useRef<HTMLDivElement>(null),
			element: <NewContractTypeStep contract={{ ...contract }} setContract={onContractChange} />,
			animationMs: 70,
		},
		{
			label: t('moveOrChangeLabel'),
			key: 'moveOrChange',
			ref: useRef<HTMLDivElement>(null),
			element: <MoveOrChangeStep contract={{ ...contract }} setContract={onContractChange} />,
			animationMs: 70,
		},
		{
			label: t('lockInDetailsLabel'),
			key: 'lockInDetails',
			element: (
				<LockInDetailsStep
					contract={contract ?? {}}
					setSelectedLockIn={setLockIn}
					lockInContracts={priceLockContracts
						?.filter(e => dayjs(e.validFrom).isBefore(dayjs().add(90, 'days')))
						.filter(e => dayjs(e.validFrom).isAfter(dayjs()))}
				/>
			),
			ref: useRef<HTMLDivElement>(null),
			animationMs: 70,
			disabled: !priceLockContracts?.length,
		},
		{
			label: t('billingMethodLabel'),
			key: 'billingMethod',
			ref: useRef<HTMLDivElement>(null),
			element: <BillingMethodStep contract={{ ...contract }} setContract={onContractChange} />,
			animationMs: 70,
		},
		{
			label: t('termsAndConditions'),
			key: 'termsAndConditions',
			ref: useRef<HTMLDivElement>(null),
			element: (
				<TermsAndConditionsStep
					contract={{ ...contract, ...(isAnonymousUser ? form.watch('contract') : {}) }}
					setContract={onContractChange}
					termsAccepted={termsAccepted}
					setTermsAccepted={setTermsAccepted}
					offer={activeOffer}
				/>
			),
			animationMs: 110,
		},
	]

	return (
		<ModalWrapper>
			{!!product && (dhUser || isAnonymousUser) ? (
				<div className='content-container mb-4'>
					<Stepper
						steps={steps}
						activeStep={activeStep}
						onNext={handleNext}
						onPrev={handlePrev}
						form={form}
					/>
				</div>
			) : (
				<div className='loading-container text-brand-primary-100-light dark:text-brand-primary-100-dark'>
					<IonSpinner className='text-brand-primary-100-light dark:text-brand-primary-100-dark' />
					<span className='ion-spinner-description'>{t('loading')}</span>
				</div>
			)}
		</ModalWrapper>
	)
}

export default CreateContractModal
